import type { SupabaseClient } from "@supabase/supabase-js";

// Account alert / channel policy (product lock 2026-08-07 §7.27-7.30, design D4, plate B).
//
// The account sets a FLOOR: for each event class, which channels fire. Members may add
// notifications for themselves later, never subtract. Reads and writes are hosted RPCs only - the
// browser never touches the policy tables.
//
// SERVER DEPENDENCY: these RPCs are Codex's Wave 3A slice
// (2026-08-07-prd-001-account-alert-channel-policy-db-parallel-brief.md) and are not deployed yet.
// The names below are pinned from that brief and asserted in tests. Until they exist, the reader
// returns status "unavailable" and the panel says so: it does NOT render a matrix of
// client-invented defaults, because an admin who sees switches assumes they were saved.

export const LIST_ALERT_CHANNEL_POLICIES_RPC = "list_account_alert_channel_policies";
export const UPSERT_ALERT_CHANNEL_POLICY_RPC = "upsert_account_alert_channel_policy";
export const GET_ALERT_QUIET_HOURS_RPC = "get_account_alert_quiet_hours";
export const UPSERT_ALERT_QUIET_HOURS_RPC = "upsert_account_alert_quiet_hours";

export interface AlertChannelPolicyRow {
  readonly eventKey: string;
  readonly inApp: boolean;
  readonly push: boolean;
  readonly email: boolean;
  readonly sms: boolean;
}

export interface AlertQuietHours {
  readonly enabled: boolean;
  /** Wall-clock "HH:MM" in `timeZone`, or null when never configured. */
  readonly startLocal: string | null;
  readonly endLocal: string | null;
  /** IANA zone, or null when never configured. */
  readonly timeZone: string | null;
}

export const QUIET_HOURS_OFF: AlertQuietHours = {
  enabled: false,
  startLocal: null,
  endLocal: null,
  timeZone: null,
};

export type AlertChannelPolicyLoad =
  | {
      readonly status: "ready";
      readonly rows: readonly AlertChannelPolicyRow[];
      readonly quietHours: AlertQuietHours;
    }
  | { readonly status: "unavailable"; readonly reason: string };

export const ALERT_POLICY_UNAVAILABLE =
  "Alert and channel policies are not available on this server yet. No delivery rules have been changed.";

/**
 * The channel columns, in plate-B order. `sms` is present so the matrix can show it as
 * unavailable; it is never written as true (see toSmsWrite).
 */
export const ALERT_CHANNELS = ["inApp", "push", "email", "sms"] as const;
export type AlertChannel = (typeof ALERT_CHANNELS)[number];

export const ALERT_CHANNEL_LABELS: Record<AlertChannel, string> = {
  inApp: "In-app",
  push: "Push",
  email: "Email",
  sms: "SMS",
};

/** SMS has no delivery path yet (lock 7.30). The column is shown, never sent as true. */
export const SMS_AVAILABLE = false;

export interface AlertEventCopy {
  readonly label: string;
  readonly definition: string;
  /**
   * Safety events the account cannot switch off, and which quiet hours can never hold
   * (lock D4, SOS.*). Presentation only - the server is the authority and refuses the write.
   */
  readonly alwaysDelivers?: readonly AlertChannel[];
}

/**
 * Plate-B labels and one-line definitions for the event keys the DB brief pins. "Late clock-in is
 * meaningless without 'more than 10 minutes past shift start'", so every known key carries its
 * definition. Keys the server adds later fall back to a humanised label and no invented meaning.
 */
export const ALERT_EVENT_COPY: Record<string, AlertEventCopy> = {
  sos: {
    label: "Duress or SOS",
    definition: "Cannot be switched off - safety event.",
    alwaysDelivers: ["inApp", "push"],
  },
  duress: {
    label: "Duress or SOS",
    definition: "Cannot be switched off - safety event.",
    alwaysDelivers: ["inApp", "push"],
  },
  missed_checkpoint: {
    label: "Checkpoint missed",
    definition: "Window closed with no capture.",
  },
  late_clock_in: {
    label: "Late clock-in",
    definition: "More than 10 minutes past shift start.",
  },
  schedule_change: {
    label: "Shift assigned or changed",
    definition: "Includes cancellations and swaps.",
  },
  feed_require_ack: {
    label: "Post needs acknowledgement",
    definition: "Reminds daily until acknowledged.",
  },
  feed_announcement: {
    label: "New announcement",
    definition: "Ordinary Feed and channel posts.",
  },
  geofence: {
    label: "Left site boundary",
    definition: "A clocked-in guard is outside the site polygon.",
  },
  field_message: {
    label: "Messages",
    definition: "Direct and crew. Quiet hours apply.",
  },
  sync_failure: {
    label: "Device sync failure",
    definition: "Captures stuck in a queue over 30 minutes.",
  },
};

export function alertEventLabel(eventKey: string): string {
  const copy = ALERT_EVENT_COPY[eventKey];
  if (copy !== undefined) return copy.label;
  const words = eventKey.replace(/[_-]+/g, " ").trim();
  return words.length === 0 ? eventKey : words.charAt(0).toUpperCase() + words.slice(1);
}

/** Empty for an unknown key: better a missing definition than an invented one. */
export function alertEventDefinition(eventKey: string): string {
  return ALERT_EVENT_COPY[eventKey]?.definition ?? "";
}

/** True when this channel is a safety floor for this event and must render locked-on. */
export function isLockedChannel(eventKey: string, channel: AlertChannel): boolean {
  return ALERT_EVENT_COPY[eventKey]?.alwaysDelivers?.includes(channel) ?? false;
}

export function accountEmailAvailable(eventKey: string): boolean {
  return eventKey !== "field_message";
}

/** Event keys quiet hours can never hold, in row order, for the quiet-hours exception sentence. */
export function quietHoursExemptLabels(rows: readonly AlertChannelPolicyRow[]): string[] {
  const labels: string[] = [];
  for (const row of rows) {
    if ((ALERT_EVENT_COPY[row.eventKey]?.alwaysDelivers?.length ?? 0) > 0) {
      const label = alertEventLabel(row.eventKey);
      if (!labels.includes(label)) labels.push(label);
    }
  }
  return labels;
}

// PostgREST / Postgres codes meaning "this RPC is not deployed here", as opposed to "the call
// failed". Mirrors the sibling check in packages/shared/src/pending-invites.ts; kept local because
// the console has no view-fallback path to share with it.
const MISSING_RPC_CODES: ReadonlySet<string> = new Set(["42883", "PGRST202"]);

export function isMissingPolicyRpc(error: unknown): boolean {
  if (error === null || typeof error !== "object") return false;
  const code = (error as { code?: unknown }).code;
  if (typeof code === "string" && MISSING_RPC_CODES.has(code)) return true;
  const message = (error as { message?: unknown }).message;
  return typeof message === "string" && message.toLowerCase().includes("could not find the function");
}

export function mapAlertChannelPolicyRow(raw: unknown): AlertChannelPolicyRow | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const eventKey = typeof row.event_key === "string" ? row.event_key.trim() : "";
  if (eventKey.length === 0) return null;
  return {
    eventKey,
    inApp: row.in_app === true || row.channel_in_app === true,
    push: row.push === true || row.channel_push === true,
    email: accountEmailAvailable(eventKey) && (row.email === true || row.channel_email === true),
    // A server that somehow reports SMS on is still shown as off: there is no delivery path.
    sms: SMS_AVAILABLE && (row.sms === true || row.channel_sms === true),
  };
}

export function mapAlertQuietHours(raw: unknown): AlertQuietHours {
  const row = Array.isArray(raw) ? raw[0] : raw;
  if (row === null || typeof row !== "object") return QUIET_HOURS_OFF;
  const record = row as Record<string, unknown>;
  return {
    enabled: record.enabled === true,
    startLocal: normalizeClockTime(record.start_local),
    endLocal: normalizeClockTime(record.end_local),
    timeZone: typeof record.time_zone === "string" && record.time_zone.trim().length > 0 ? record.time_zone.trim() : null,
  };
}

/** Postgres `time` arrives as "23:00:00"; the time input wants "23:00". */
export function normalizeClockTime(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const match = /^(\d{2}):(\d{2})/.exec(value.trim());
  return match === null ? null : `${match[1]}:${match[2]}`;
}

/**
 * Matrix plus quiet hours for the account. Throws (so useReadModel shows its error state) only on
 * a real failure; a server without these RPCs returns "unavailable" instead.
 */
export async function fetchAlertChannelPolicy(
  client: SupabaseClient,
  accountId: string,
): Promise<AlertChannelPolicyLoad> {
  let rowsRaw: unknown;
  try {
    const { data, error } = await client.rpc(LIST_ALERT_CHANNEL_POLICIES_RPC, { p_account_id: accountId });
    if (error !== null) {
      if (isMissingPolicyRpc(error)) {
        return { status: "unavailable", reason: ALERT_POLICY_UNAVAILABLE };
      }
      throw new Error("Could not load alert and channel policies.");
    }
    rowsRaw = data;
  } catch (caught) {
    throw caught instanceof Error ? caught : new Error("Could not load alert and channel policies.");
  }

  const rows: AlertChannelPolicyRow[] = [];
  if (Array.isArray(rowsRaw)) {
    for (const raw of rowsRaw) {
      const row = mapAlertChannelPolicyRow(raw);
      if (row !== null) rows.push(row);
    }
  }

  // Quiet hours are optional and off by default (D4). A server that ships the matrix but not the
  // quiet-hours RPC still gets a working matrix, with quiet hours shown as off.
  let quietHours = QUIET_HOURS_OFF;
  try {
    const { data, error } = await client.rpc(GET_ALERT_QUIET_HOURS_RPC, { p_account_id: accountId });
    if (error === null) {
      quietHours = mapAlertQuietHours(data);
    } else if (!isMissingPolicyRpc(error)) {
      throw new Error("Could not load quiet hours.");
    }
  } catch (caught) {
    throw caught instanceof Error ? caught : new Error("Could not load quiet hours.");
  }

  return { status: "ready", rows, quietHours };
}

export type AlertPolicyWriteOutcome = { ok: true } | { ok: false; reason: string };

export const SAFE_ALERT_POLICY_FAILURE = "Couldn't save that alert policy change - please try again.";

/**
 * Verbatim refusal sentences raised by the two upsert RPCs in migration
 * 20260807210000_account_alert_channel_policy.sql. Transcribed from the shipped SQL, not from the
 * brief that preceded it. Anything else - including a missing-RPC error and the internal
 * "caller mismatch" guards - collapses to the generic sentence, so the surface cannot become an
 * error oracle. Read failures never reach here; they surface a fixed sentence of their own.
 */
export const KNOWN_ALERT_POLICY_FAILURES: ReadonlySet<string> = new Set([
  // upsert_account_alert_channel_policy
  "Account id is required to manage account alert channel policy.",
  "Authentication is required to manage account alert channel policy.",
  "Alert channel selections are required.",
  "Alert event key is not supported.",
  "SMS alert delivery is not available.",
  "SOS and duress alerts require in-app and push delivery.",
  "Email delivery is not available for Messages.",
  // upsert_account_alert_quiet_hours
  "Account id is required to manage account alert quiet hours.",
  "Authentication is required to manage account alert quiet hours.",
  "Quiet hours settings are required.",
  "Quiet hours start and end must be different.",
  "Quiet hours time zone is not recognized.",
  // both
  "Caller is not authorized to manage policies for this account.",
  "This account is contract-locked; only a platform superadmin can change its policies.",
]);

export function safeAlertPolicyReason(error: unknown): string {
  const message =
    error !== null && typeof error === "object" && typeof (error as { message?: unknown }).message === "string"
      ? ((error as { message: string }).message)
      : "";
  return KNOWN_ALERT_POLICY_FAILURES.has(message) ? message : SAFE_ALERT_POLICY_FAILURE;
}

async function runAlertPolicyWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<AlertPolicyWriteOutcome> {
  try {
    const { error } = await client.rpc(fn, params);
    return error === null ? { ok: true } : { ok: false, reason: safeAlertPolicyReason(error) };
  } catch {
    return { ok: false, reason: SAFE_ALERT_POLICY_FAILURE };
  }
}

/** One event row. `p_sms` is hard-wired false until SMS delivery exists (lock 7.30). */
export async function upsertAlertChannelPolicy(
  client: SupabaseClient,
  accountId: string,
  row: AlertChannelPolicyRow,
): Promise<AlertPolicyWriteOutcome> {
  return runAlertPolicyWrite(client, UPSERT_ALERT_CHANNEL_POLICY_RPC, {
    p_account_id: accountId,
    p_event_key: row.eventKey,
    p_in_app: row.inApp,
    p_push: row.push,
    p_email: accountEmailAvailable(row.eventKey) && row.email,
    p_sms: SMS_AVAILABLE && row.sms,
  });
}

export async function upsertAlertQuietHours(
  client: SupabaseClient,
  accountId: string,
  quietHours: AlertQuietHours,
): Promise<AlertPolicyWriteOutcome> {
  return runAlertPolicyWrite(client, UPSERT_ALERT_QUIET_HOURS_RPC, {
    p_account_id: accountId,
    p_enabled: quietHours.enabled,
    p_start_local: quietHours.enabled ? quietHours.startLocal : null,
    p_end_local: quietHours.enabled ? quietHours.endLocal : null,
    p_time_zone: quietHours.enabled ? quietHours.timeZone : null,
  });
}

/** Rows whose channels differ from what the server returned. Save writes only these. */
export function changedPolicyRows(
  saved: readonly AlertChannelPolicyRow[],
  draft: readonly AlertChannelPolicyRow[],
): AlertChannelPolicyRow[] {
  const savedByKey = new Map(saved.map((row) => [row.eventKey, row]));
  return draft.filter((row) => {
    const before = savedByKey.get(row.eventKey);
    if (before === undefined) return true;
    return before.inApp !== row.inApp || before.push !== row.push || before.email !== row.email || before.sms !== row.sms;
  });
}

export function quietHoursChanged(saved: AlertQuietHours, draft: AlertQuietHours): boolean {
  return (
    saved.enabled !== draft.enabled ||
    saved.startLocal !== draft.startLocal ||
    saved.endLocal !== draft.endLocal ||
    saved.timeZone !== draft.timeZone
  );
}

/** Pre-network check so an incomplete window never reaches the server. */
export function validateQuietHours(draft: AlertQuietHours): { ok: true } | { ok: false; reason: string } {
  if (!draft.enabled) return { ok: true };
  if (draft.startLocal === null || draft.endLocal === null) {
    return { ok: false, reason: "Set a start and end time for quiet hours." };
  }
  if (draft.startLocal === draft.endLocal) {
    return { ok: false, reason: "Quiet hours start and end cannot be the same time." };
  }
  if (draft.timeZone === null || draft.timeZone.trim().length === 0) {
    return { ok: false, reason: "Choose the time zone quiet hours follow." };
  }
  return { ok: true };
}
